import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useEquipmentProjects } from "@/hooks/useEquipmentProjects";
import { format } from "date-fns";
import { formatProjectName } from "@/lib/format-project-name";

interface EquipmentProjectsListProps {
  equipmentId: string;
}

export function EquipmentProjectsList({
  equipmentId,
}: EquipmentProjectsListProps) {
  const { data: projects = [], isLoading } = useEquipmentProjects(equipmentId);

  if (isLoading) {
    return (
      <p className="text-muted-foreground text-center py-8">
        Loading projects...
      </p>
    );
  }

  if (projects.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        No projects use this equipment yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {projects.map((project) => (
        <Link
          key={project.id}
          to={`/projects/${project.id}`}
          className="flex items-center justify-between p-3 rounded-lg border text-inherit no-underline hover:bg-muted/50 transition-colors"
          data-testid={`equipment-project-${project.id}`}
        >
          <div>
            <p
              className="font-medium"
              data-testid={`equipment-project-name-${project.id}`}
            >
              {formatProjectName(project.name, project.event_start_date)}
            </p>
            <p className="text-xs text-muted-foreground">
              {project.event_start_date &&
                format(new Date(project.event_start_date), "MMM d, yyyy")}
              {project.event_end_date &&
                ` - ${format(new Date(project.event_end_date), "MMM d, yyyy")}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {project.status && (
              <Badge variant="outline" className="capitalize">
                {project.status.replace("_", " ")}
              </Badge>
            )}
            {project.types?.map((type: string) => (
              <Badge key={type} variant="secondary" className="capitalize">
                {type}
              </Badge>
            ))}
          </div>
        </Link>
      ))}
    </div>
  );
}
